import React, { ComponentType, ReactNode, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { ErrorBoundary } from '@datadog/browser-rum-react';
import {
  CssBase,
  setAppElement,
  setServerSideRendered,
} from '@stitch-fix/mode-react';
import ErrorPage from '../ErrorPage';
import { ClientAuthProvider } from '../ClientAuthContext';
import { ClientI18nProvider, useClientI18n } from '../ClientI18nContext';
import { createGraphQLApiProvider } from '../GraphQLApiProvider';
import ClientGtm from '../ClientGtm';
import { hasLocalAssets } from '../utils/settings';

// The magic param allows us to automatically log clients in when they get an email from us
const magicAuthParam = 'email_validation';

// Let `mode-react` know that this app is server-side rendered
// so that media query utilities are handled properly.
// See: https://mode-react.daylight.stitchfix.com/?path=/story/ssr--page
setServerSideRendered();

export interface ClientAppProps extends AppProps {
  /**
   * The GraphQL API Provider to wrap around the page.
   * Uses a default Provider when unspecified (`undefined`).
   * Use `null` to have no Provider.
   */
  GraphQLApiProvider?: ComponentType<{ children: ReactNode }> | null;
}

const Meta = () => {
  const { locale, region } = useClientI18n();

  return (
    <Head>
      <meta name="viewport" content="width=device-width,initial-scale=1" />
      <meta name="stitch-fix:locale" content={locale} />
      <meta name="stitch-fix:region" content={region} />
      <meta
        name="apple-itunes-app"
        content="app-id=1022579925, affiliate-data=ct=smart_app_banner&pt=117196399"
      />
      <meta
        name="facebook-domain-verification"
        content="x5k145dyv664rwswb6vlof99nwjhkd"
      />
    </Head>
  );
};

const EmptyGraphQLApiProvider = ({ children }: { children: ReactNode }) => (
  <>{children}</>
);

const useMagicAuth = () => {
  const router = useRouter();

  useEffect(() => {
    if (router.isReady) {
      const query = { ...router.query };

      if (query[magicAuthParam]) {
        // We want to remove the magicAuthParam off of URLs so they are not shared
        // If someone copies the URL out of the browser
        delete query[magicAuthParam];

        router.replace({ query }, undefined, { shallow: true });
      }
    }
    // We don't want to include `router` in the deps because it causes an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query, router.replace]);
};

// Note: client-analytics-reporter already handles setting up Datadog user
// and metadata tracking, so we don't need to duplicate that here

/**
 * Base custom Next App for client-facing applications. It provides client auth
 * information in the React content as well as Mode React base styling
 */
export const ClientApp = ({
  Component,
  GraphQLApiProvider: RequestedGraphQLApiProvider,
  pageProps,
}: ClientAppProps) => {
  const GraphQLApiProvider = useMemo(() => {
    if (RequestedGraphQLApiProvider === undefined) {
      // Ensure a unique apollo client is created for each SSR request, but is memoized in-between
      // renders on the client-side
      return createGraphQLApiProvider().GraphQLApiProvider;
    }

    if (RequestedGraphQLApiProvider === null) {
      return EmptyGraphQLApiProvider;
    }

    return RequestedGraphQLApiProvider;
  }, [RequestedGraphQLApiProvider]);

  useEffect(() => {
    // Sets the `root` element for Modals in mode-react
    setAppElement('#root');

    // After hydration, new components will be rendered client-side, so mode-react no longer
    // needs to optimize for SSR, and can instead optimize for client-side rendering, avoiding
    // two-pass renders in some cases:
    setServerSideRendered(false);
  }, []);

  useMagicAuth();

  // We want to disable GTM in test and dev environments. In e2e browserstack tests, it causes bad
  // interactions with mock-service-worker and next middleware, causing pages to continuously
  // refresh, and makes a lot of unnecessary requests for tracking scripts.
  const showGTM = !hasLocalAssets();

  return (
    <ErrorBoundary fallback={ErrorPage}>
      <CssBase>
        <ClientAuthProvider>
          <ClientI18nProvider>
            <GraphQLApiProvider>
              <Meta />
              {showGTM && <ClientGtm />}
              <div id="root">
                <Component {...pageProps} />
              </div>
            </GraphQLApiProvider>
          </ClientI18nProvider>
        </ClientAuthProvider>
      </CssBase>
    </ErrorBoundary>
  );
};

export default ClientApp;
